import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Mail, CheckCircle, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { z } from 'zod';
import { trackLeadGeneration } from '@/lib/analytics';

const emailSchema = z.string().email("Please enter a valid email").max(255);

interface NewsletterSignupProps {
  source?: string;
  variant?: 'default' | 'compact';
  className?: string;
}

export const NewsletterSignup = ({ 
  source = 'blog', 
  variant = 'default',
  className = '' 
}: NewsletterSignupProps) => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate email
    const result = emailSchema.safeParse(email);
    if (!result.success) {
      toast.error(result.error.errors[0].message);
      return;
    }

    setIsSubmitting(true);

    try {
      const { data, error } = await supabase.functions.invoke('subscribe-newsletter', {
        body: { email: email.trim(), source }
      });

      if (error) throw error;

      if (data.success) {
        setIsSuccess(true);
        toast.success(data.message);
        setEmail('');
        trackLeadGeneration('contact_form', { form_type: 'newsletter', source });
      } else {
        toast.error(data.error || 'Failed to subscribe');
      }
    } catch (error) {
      console.error('Newsletter signup error:', error);
      toast.error('Failed to subscribe. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className={`bg-primary/10 border border-primary/20 rounded-xl p-6 text-center ${className}`}>
        <CheckCircle className="w-12 h-12 text-primary mx-auto mb-3" />
        <h4 className="font-semibold text-foreground mb-2">You're In! 🎉</h4>
        <p className="text-muted-foreground text-sm">
          Thanks for subscribing! Watch your inbox for grooming tips and exclusive offers.
        </p>
      </div>
    );
  }

  if (variant === 'compact') {
    return (
      <form onSubmit={handleSubmit} className={`flex gap-2 ${className}`}>
        <Input
          type="email"
          placeholder="Enter your email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="flex-1"
          disabled={isSubmitting}
          required
        />
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Subscribe'}
        </Button>
      </form>
    );
  }

  return (
    <div className={`bg-muted/50 border border-border rounded-xl p-6 ${className}`}>
      <div className="flex items-center gap-3 mb-4">
        <div className="bg-primary/10 p-2 rounded-lg">
          <Mail className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h4 className="font-semibold text-foreground">Get Grooming Tips</h4>
          <p className="text-sm text-muted-foreground">
            Weekly tips + exclusive subscriber discounts
          </p>
        </div>
      </div>
      <form onSubmit={handleSubmit} className="flex gap-2">
        <Input
          type="email"
          placeholder="your@email.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="flex-1"
          disabled={isSubmitting}
          required
        />
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            'Subscribe'
          )}
        </Button>
      </form>
      <p className="text-xs text-muted-foreground mt-3">
        No spam, ever. Unsubscribe anytime.
      </p>
    </div>
  );
};
